import type { Theme } from 'app/providers/theme';
import type React from 'react';
import styled, { keyframes, useTheme } from 'styled-components';
import Typography from 'app/ui/components/atoms/typography';

type ButtonTypes = 'primary' | 'secondary' | 'secondary-white' | 'curved-grey' | 'curved';
type ButtonStates = 'default' | 'disabled';
type IconPosition = 'left' | 'right';

export interface Props {
  label: string;
  state?: ButtonStates;
  iconPosition?: IconPosition;
  icon?: JSX.Element;
  onClick?: () => void;
  type?: ButtonTypes;
  fullWidthMob?: boolean;
  className?: string;
  isLoading?: boolean;
}

interface BaseButtonProps {
  theme: Theme;
  $variant?: ButtonTypes;
  $fullWidthMob?: boolean;
}

interface IconWrapperProps {
  theme: Theme;
  $iconPosition?: IconPosition;
}

const BaseButton = styled.button<BaseButtonProps>`
  ${({ theme, $variant, $fullWidthMob }) => {
    const { typography, colors, spacing, media } = theme;

    const getBorderColor = () => {
      switch ($variant) {
        case 'secondary':
          return colors.focoltone;
        case 'secondary-white':
          return colors.white;
        case 'curved-grey':
          return colors.blackSwan;
        default:
          return 'transparent';
      }
    };

    const getBackgroundColor = () => {
      switch ($variant) {
        case 'secondary':
        case 'secondary-white':
        case 'curved-grey':
          return 'transparent';
        case 'curved':
          return colors.focoltoneWithTransparency;
        default:
          return colors.focoltone;
      }
    };

    const getHoverColor = () => {
      switch ($variant) {
        case 'secondary':
          return colors.caparol;
        case 'secondary-white':
        case 'curved':
        case 'curved-grey':
          return colors.knop;
        default:
          return colors.black;
      }
    };

    const getHoverBackgroundColor = () => {
      switch ($variant) {
        case 'primary':
          return colors.caparol;
        case 'curved-grey':
          return colors.black;
        case 'curved':
          return colors.focoltoneWithTransparencyHover;
        default:
          return 'transparent';
      }
    };

    const getBorderRadius = () => {
      if ($variant?.includes('curved')) {
        return '1.5rem';
      }
      return '0.5rem';
    };

    return `
      align-items: baseline;
      background: ${getBackgroundColor()};
      border-radius: ${getBorderRadius()};
      border: 0.0625rem solid ${getBorderColor()};
      cursor: pointer;
      line-height: ${typography.lineHeight};
      padding: ${spacing(2)} ${spacing(4)};
      font-size: ${typography.tiny};

      &:hover {
        background: ${getHoverBackgroundColor()};
        border: 0.0625rem solid ${getHoverColor()};
        color: ${getHoverColor()};
      }

      &:active {
        border: 0.0625rem solid ${colors.pascol};
        color: ${colors.pascol};
      }

      &:disabled {
        border: 0.0625rem solid ${colors.blackSwan};
        color: ${colors.knop};
        cursor: not-allowed;
        background: ${colors.blackSwan};
      }

      ${media.medium(
        (theme: Theme) => `
        padding: ${theme.spacing(2)} ${theme.spacing(4.5)};
      `,
      )}

      ${
        $fullWidthMob &&
        `
        width: 100%;
        justify-content: center;
        ${media.medium(
          () => `
            width: auto;
            justify-content: initial;
        `,
        )}
      `
      }
    `;
  }}
`;

const IconWrapper = styled.span<IconWrapperProps>`
  display: none;

  ${({ $iconPosition, theme: { spacing } }) =>
    $iconPosition === 'right' ? `margin-left: ${spacing(2)};` : `margin-right: ${spacing(2)};`}

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        display: inline-flex;
        align-items: center;
      `,
    )}
  `}
`;

const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const stretch = keyframes`
  0% {
    stroke-dasharray: 0, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 75, 150;
    stroke-dashoffset: -25;
  }
  100% {
    stroke-dashoffset: -100;
  }
`;

const LoadingIconWrapper = styled.output`
  display: inline-flex;
  align-items: center;
  margin-left: ${({ theme: { spacing } }) => spacing(2)};
`;

const LoadingIcon = styled.svg<{ $color: string }>`
  height: 1rem;
  width: 1rem;
  transform-origin: center;
  animation: ${rotate} 2s linear infinite;
  overflow: visible;

  circle {
    fill: none;
    stroke: ${({ $color }) => $color};
    stroke-linecap: round;
  }

  .track {
    opacity: 0.15;
  }

  .car {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
    animation: ${stretch} 1.5s ease-in-out infinite;
  }
`;

const StyledTypography = styled(Typography)`
  font-size: ${({ theme }) => theme.typography.xsmall};

  ${({ theme }) => `
    ${theme.media.medium(
      () => `
        font-size: ${theme.typography.small};
      `,
    )}
  `}
`;

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const Button: React.FC<Props> = ({
  label,
  state = 'default',
  iconPosition = 'right',
  icon,
  onClick,
  type = 'primary',
  fullWidthMob,
  className,
  isLoading = false,
}) => {
  const isIconLeft = icon && iconPosition === 'left';
  const isIconRight = icon && iconPosition === 'right' && !isLoading;
  const { colors } = useTheme();

  const getTextColor = () => {
    switch (type) {
      case 'secondary':
        return colors.focoltone;
      case 'secondary-white':
      case 'curved':
      case 'curved-grey':
        return colors.white;
      default:
        return colors.black;
    }
  };

  const getTypographyVariant = () => {
    switch (type) {
      case 'curved':
      case 'curved-grey':
        return 'caption';
      default:
        return 'button';
    }
  };

  return (
    <BaseButton
      type="button"
      $variant={type}
      onClick={onClick}
      disabled={state === 'disabled' || isLoading}
      $fullWidthMob={fullWidthMob}
      className={className}
    >
      <LabelWrapper>
        {isIconLeft && <IconWrapper $iconPosition={iconPosition}>{icon}</IconWrapper>}
        <StyledTypography variant={getTypographyVariant()} color={getTextColor()}>
          {label}
        </StyledTypography>
        {isLoading && (
          <LoadingIconWrapper aria-label="Loading">
            <LoadingIcon viewBox="0 0 40 40" $color={getTextColor()}>
              <circle className="track" cx="20" cy="20" r="17.5" pathLength="100" strokeWidth="4" />
              <circle className="car" cx="20" cy="20" r="17.5" pathLength="100" strokeWidth="4" />
            </LoadingIcon>
          </LoadingIconWrapper>
        )}
        {isIconRight && <IconWrapper $iconPosition={iconPosition}>{icon}</IconWrapper>}
      </LabelWrapper>
    </BaseButton>
  );
};

export default Button;
